import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createUseStyles, Button, Steps } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { TransitionGroup, FadeTransition } from 'components/Transitions';
import { Layout } from '../components';
import { InviteUsers, SegmentAccounts, OrdersStates, SetupPipeline, Summary } from './components';
import { TABLES } from '../constants';

import styles from './styles.js';

const useStyles = createUseStyles(styles, 'OnboardingWizard');

const trackingEvents = [
    'firstStepOnboarding',
    'secondStepOnboarding',
    'thirdStepOnboarding',
    'fourthStepOnboarding',
    'fifthStepOnboarding',
];

const OnboardingWizard = ({
    logo,
    getUsers,
    setUsers,
    getList,
    setList,
    userRoles,
    handleError,
    customEmailValidation,
    productModeCode,
    onSubmit,
    settings,
    logEvent,
}) => {
    const isFirstLoad = useRef(true);
    const currentStep = useRef(0);
    const [state, setState] = useState({
        loading: false,
    });
    const [steps, setSteps] = useState([
        { isCompleted: false },
        { isCompleted: false },
        { isCompleted: false },
        { isCompleted: false },
        { isCompleted: false },
    ]);
    const [count, setCount] = useState({
        [TABLES.ACCOUNT_TYPES]: 0,
        [TABLES.INVITED_USERS]: 0,
        [TABLES.OPPORTUNITY_STATUS]: 0,
        [TABLES.SALES_ORDER_STATUS]: 0,
    });

    const classes = useStyles();
    const { t } = useWeb5Context();

    const handleTracking = useCallback(() => {
        logEvent &&
            logEvent({
                event: 'sageCRM',
                functionality: trackingEvents[currentStep.current],
            });
    }, [logEvent]);

    useEffect(() => {
        if (!isFirstLoad.current) return;
        isFirstLoad.current = false;
        handleTracking();
    }, [handleTracking]);

    const handleSubmit = useCallback(() => {
        setState((state) => ({
            ...state,
            loading: true,
        }));
        onSubmit && onSubmit();
    }, [onSubmit]);

    const handleStepChange = useCallback(
        (e, delta = 1) => {
            e.preventDefault();
            currentStep.current = currentStep.current + delta;
            const newSteps = steps.reduce(
                (arr, step, index) => [
                    ...arr,
                    {
                        ...step,
                        isCompleted: currentStep.current > index,
                    },
                ],
                [],
            );
            setSteps(newSteps);

            if (currentStep.current === steps.length) {
                handleSubmit();
            } else {
                handleTracking();
            }
        },
        [handleSubmit, handleTracking, steps],
    );

    const handleSetCount = useCallback(
        (key, value) =>
            setCount({
                ...count,
                [key]: value,
            }),
        [count],
    );

    const inviteUsersProps = useMemo(
        () => ({
            getUsers,
            setUsers,
            userRoles,
            customEmailValidation,
            handleError,
            productModeCode,
            handleSetCount,
        }),
        [
            customEmailValidation,
            getUsers,
            setUsers,
            userRoles,
            handleError,
            productModeCode,
            handleSetCount,
        ],
    );

    const listsProps = useMemo(
        () => ({
            getList,
            setList,
            handleError,
            handleSetCount,
        }),
        [getList, handleError, setList, handleSetCount],
    );

    const getStep = useCallback(
        (step) => {
            step = step >= steps.length ? steps.length - 1 : step;
            return [
                <InviteUsers key="invite-users" {...inviteUsersProps} />,
                <SegmentAccounts key="segment-accounts" {...listsProps} />,
                <OrdersStates key="orders-states" {...listsProps} />,
                <SetupPipeline key="setup-pipeline" {...listsProps} />,
                <Summary key="summary" count={count} settings={settings?.summary} />,
            ][step];
        },
        [inviteUsersProps, listsProps, steps.length, count, settings],
    );

    const getNavActions = useMemo(() => {
        return (
            <div className={classes.nav}>
                {currentStep.current > 0 && (
                    <Button
                        size="big"
                        type="secondary"
                        icon="arrowBack"
                        onClick={(e) => handleStepChange(e, -1)}
                        isDisabled={state.loading}
                    >
                        {t('action_back')}
                    </Button>
                )}
                <Button
                    size="big"
                    type="primary"
                    icon={state.loading ? null : 'arrowForward'}
                    iconPosition="right"
                    onClick={handleStepChange}
                    isLoading={state.loading}
                >
                    {t(
                        currentStep.current < steps.length - 1
                            ? 'action_next'
                            : 'action_start_enjoy',
                    )}
                </Button>
            </div>
        );
    }, [steps.length, classes.nav, t, state.loading, handleStepChange]);

    return (
        <Layout className={classes.layout}>
            <div className={classes.logo}>{logo}</div>
            <Steps steps={steps} currentStep={currentStep.current} className={classes.stepper} />
            <TransitionGroup>
                <FadeTransition transitionKey={`${currentStep.current}`} show>
                    {getStep(currentStep.current)}
                </FadeTransition>
            </TransitionGroup>
            {getNavActions}
        </Layout>
    );
};

export default memo(OnboardingWizard);
